import React, { useMemo, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Checkbox,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  EditOutlined as EditOutlinedIcon,
  GpsFixedRounded as TargetIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  ScheduleOutlined as ScheduleOutlinedIcon,
  VisibilityOffOutlined as VisibilityOffIcon,
  VisibilityOutlined as VisibilityIcon,
} from '@mui/icons-material';
import type { Trip, TouchPoint, TouchPointHoverPreview } from '../../models/types';
import DenseEntityCardBody from '../cards/DenseEntityCardBody';
import TouchPointSequenceRail from '../cards/TouchPointSequenceRail';
import TripCardDataRail from '../cards/TripCardDataRail';
import {
  buildTripCardModel,
  getTripAccentColor,
} from '../../presentation/cards/models';

interface TripCardProps {
  trip: Trip;
  tenant?: string;
  isSelected: boolean;
  isVisible: boolean;
  isEditSourceTrip?: boolean;
  isHighlighted?: boolean;
  selectedEditTouchPointIds?: string[];
  hideDriverContractFields?: boolean;
  onSelect: () => void;
  onVisibility: () => void;
  onFocus?: (lngLat: [number, number]) => void;
  onFocusTrip?: () => void;
  onFocusTouchPoint?: (tp: TouchPoint) => void;
  onHoverTouchPoint?: (preview: TouchPointHoverPreview | null) => void;
  onToggleEditTouchPoint?: (tp: TouchPoint) => void;
  onOpenTimeline?: () => void;
  onStartTripEdit?: () => void;
  onCancelTripEdit?: () => void;
}

const TripCard: React.FC<TripCardProps> = ({
  trip,
  tenant,
  isSelected,
  isVisible,
  isEditSourceTrip = false,
  isHighlighted = false,
  selectedEditTouchPointIds = [],
  onSelect,
  onVisibility,
  onFocus,
  onFocusTrip,
  onFocusTouchPoint,
  onHoverTouchPoint,
  onToggleEditTouchPoint,
  onOpenTimeline,
  onStartTripEdit,
  onCancelTripEdit,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const tripColor = useMemo(() => getTripAccentColor(trip), [trip]);
  const isJumboBuy = String(tenant ?? '').toLowerCase().includes('jumbobuy');
  const model = useMemo(() => {
    const baseModel = buildTripCardModel(trip, { tenant });
    if (!isJumboBuy) {
      return baseModel;
    }

    return {
      ...baseModel,
      topTags: baseModel.topTags.filter((tag) => tag.key !== 'trip-id' && !tag.key.startsWith('slot-')),
      primaryMetrics: baseModel.primaryMetrics.filter((metric) =>
        ['weight', 'points', 'distance', 'duration', 'volume'].includes(metric.key)
      ),
      capacityTags: baseModel.capacityTags.filter((tag) => tag.key !== 'avl-crates'),
    };
  }, [isJumboBuy, tenant, trip]);
  const headerModel = useMemo(
    () => ({
      ...model,
      highlightTags: [],
      primaryMetrics: [],
      capacityTags: [],
      breachTags: [],
      footerTags: [],
      footerText: undefined,
    }),
    [model]
  );

  React.useEffect(() => {
    if (isEditSourceTrip) {
      setExpanded(true);
    }
  }, [isEditSourceTrip]);

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1.15,
        overflow: 'hidden',
        borderRadius: 2.5,
        borderColor: isEditSourceTrip
          ? alpha('#2563eb', 0.56)
          : isHighlighted
            ? alpha(tripColor, 0.72)
          : isSelected
            ? alpha(tripColor, 0.42)
            : alpha(theme.palette.divider, 0.9),
        boxShadow: isEditSourceTrip
          ? `0 14px 28px ${alpha('#2563eb', 0.12)}`
          : isHighlighted
            ? `0 16px 30px ${alpha(tripColor, 0.20)}`
          : isSelected
            ? `0 14px 28px ${alpha(tripColor, 0.14)}`
            : 'none',
        transform: isHighlighted ? 'translateY(-1px)' : 'none',
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: `0 14px 24px ${alpha(theme.palette.common.black, 0.07)}`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          bgcolor: tripColor,
        },
      }}
    >
      <Box sx={{ overflow: 'hidden', borderRadius: 'inherit', bgcolor: 'background.paper' }}>
        <Box
          sx={{
            px: 1.2,
            py: 1.05,
            pl: 1.5,
            background: isEditSourceTrip
              ? `linear-gradient(135deg, ${alpha('#dbeafe', 0.88)}, ${alpha('#eff6ff', 0.66)})`
              : isHighlighted
                ? `linear-gradient(135deg, ${alpha(tripColor, theme.palette.mode === 'dark' ? 0.18 : 0.10)}, transparent)`
              : 'transparent',
          }}
        >
          <DenseEntityCardBody
            model={headerModel}
            density="compact"
            titleIcon="vehicle"
            compactTitle
            inlineTitleWithLeading
            leading={
              <Checkbox
                checked={isSelected}
                onChange={onSelect}
                size="small"
                color="primary"
                sx={{ p: 0.2, mr: 0.15 }}
              />
            }
            actions={
              <React.Fragment>
                <Tooltip title={isVisible ? 'Hide route' : 'Show route'}>
                  <IconButton
                    aria-label="toggle visibility"
                    onClick={onVisibility}
                    size="small"
                    sx={{
                      width: 28,
                      height: 28,
                      border: '1px solid',
                      borderColor: alpha(theme.palette.text.primary, 0.12),
                      bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.02 : 0.72),
                    }}
                  >
                    {isVisible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                {onFocusTrip ? (
                  <Tooltip title="Focus trip on map">
                    <IconButton
                      aria-label="focus trip on map"
                      onClick={onFocusTrip}
                      size="small"
                      sx={{
                        width: 28,
                        height: 28,
                        border: '1px solid',
                        borderColor: alpha(theme.palette.text.primary, 0.12),
                        bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.02 : 0.72),
                      }}
                    >
                      <TargetIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                ) : null}
                {onOpenTimeline ? (
                  <Tooltip title="Open timeline">
                    <IconButton
                      aria-label="open timeline"
                      onClick={onOpenTimeline}
                      size="small"
                      sx={{
                        width: 28,
                        height: 28,
                        border: '1px solid',
                        borderColor: alpha(theme.palette.text.primary, 0.12),
                        bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.02 : 0.72),
                      }}
                    >
                      <ScheduleOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
                {onStartTripEdit || (isEditSourceTrip && onCancelTripEdit) ? (
                  <Tooltip title={isEditSourceTrip ? 'Cancel edit' : 'Edit trip'}>
                    <IconButton
                      aria-label={isEditSourceTrip ? 'cancel edit' : 'edit trip'}
                      onClick={isEditSourceTrip ? onCancelTripEdit : onStartTripEdit}
                      size="small"
                      sx={{
                        width: 28,
                        height: 28,
                        border: '1px solid',
                        borderColor: alpha(theme.palette.text.primary, 0.12),
                        bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.02 : 0.72),
                      }}
                    >
                      {isEditSourceTrip ? <CloseIcon fontSize="small" /> : <EditOutlinedIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                ) : null}
              </React.Fragment>
            }
          />
          <Box sx={{ mt: 0.75 }}>
            <TripCardDataRail
              primaryMetrics={model.primaryMetrics}
              capacityTags={model.capacityTags}
              secondaryTags={model.highlightTags}
              breachTags={model.breachTags}
              footerTags={model.footerTags}
              footerText={model.footerText}
            />
          </Box>
        </Box>

        <Divider />

        <Box sx={{ px: 1.35, py: 0.35, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 500,
              letterSpacing: 0,
            }}
          >
            {trip.tpDetails.length} touch points
          </Typography>
          <IconButton
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            aria-label="toggle touch points"
            size="small"
          >
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ px: 1.35, pb: 1.2, pt: 0.1 }}>
            <TouchPointSequenceRail
              trip={trip}
              tenant={tenant}
              onHoverTouchPoint={onHoverTouchPoint}
              onFocusTouchPoint={onFocusTouchPoint}
              onFocusLngLat={onFocus}
              isEditSourceTrip={isEditSourceTrip}
              selectedTouchPointIds={selectedEditTouchPointIds}
              onToggleTouchPoint={onToggleEditTouchPoint}
            />
          </Box>
        </Collapse>
      </Box>
    </Paper>
  );
};

export default TripCard;
